import {
  AmbientLight,
  Color,
  DirectionalLight,
  Fog,
  PerspectiveCamera,
  PointLight,
  Scene,
  WebGLRenderer,
} from 'three';
import { CombatSystem } from './combat/CombatSystem';
import { EventBus } from './core/EventBus';
import {
  loadGraphicsSettings,
  resolveGraphics,
  saveGraphicsSettings,
  type GraphicsQuality,
  type GraphicsSettings,
  type ResolvedGraphics,
} from './core/graphicsSettings';
import {
  hasContinuableSingleplayerSave,
  loadPlayerSave,
  profileFromSave,
  saveLastSessionHint,
  savePlayerSave,
  type PlayerSaveData,
} from './core/playerSave';
import { EnemyManager } from './entities/EnemyManager';
import { ITEM_CATALOG, ItemId, blockTypeToItem, getSellPrice } from './items/catalog';
import { CraftingService, InventoryManager } from './items/InventoryManager';
import { LootDropManager } from './items/LootDropManager';
import { MiningSystem } from './items/MiningSystem';
import { NetworkBridge } from './network/NetworkBridge';
import {
  emitStageEnter,
  saveWorldSnapshot,
  type MultiplayerSession,
} from './network/MultiplayerClient';
import { RemotePlayerManager } from './network/RemotePlayerManager';
import type {
  DigPatch,
  EnemyHitPacket,
  EnemySpawnPacket,
  EnemyStatePacket,
  PlayerHurtPacket,
  StageEnterPacket,
} from './network/protocol';
import {
  ENEMY_CATALOG,
  EnemyKind,
  bossKindForStage,
  isBossStage,
} from './entities/enemyCatalog';
import { InputManager } from './input/InputManager';
import { HeldToolView } from './player/HeldToolView';
import { PlayerController } from './player/PlayerController';
import {
  BlockType,
  DEFAULT_GAME_CONFIG,
  GameState,
  createDefaultProfile,
  createEmptyRunInventory,
  type ActiveRunInventory,
  type DeathMarkerEntity,
  type GameConfig,
  type PersistentPlayerProfile,
  type Vector3D,
  vec3,
} from './types';
import { DevMenu } from './ui/DevMenu';
import { InventoryUI } from './ui/InventoryUI';
import { DeathScreen } from './ui/DeathScreen';
import { MinimapUI, type MinimapMarker } from './ui/MinimapUI';
import { PauseMenu } from './ui/PauseMenu';
import { SettingsUI } from './ui/SettingsUI';
import { ShopUI } from './ui/ShopUI';
import { TouchControls } from './ui/TouchControls';
import { lookDirectionFromAngles, screenPointToWorldDirection } from './world/raycast';
import { WorldManager } from './world/WorldManager';

export interface EngineOptions {
  canvasParent: HTMLElement;
  config?: Partial<GameConfig>;
  playerId?: string;
  displayName?: string;
}

export type MultiplayerRole = 'host' | 'client' | null;

/**
 * Core runtime: fixed-timestep simulation + variable render interpolation,
 * inventory / crafting, weapons, and Spelunky-style level door transitions.
 */
export class Engine {
  readonly config: GameConfig;
  readonly events = new EventBus();
  readonly profile: PersistentPlayerProfile;
  readonly network: NetworkBridge;
  readonly inventory: InventoryManager;
  readonly crafting: CraftingService;

  private runInventory: ActiveRunInventory;
  private state: GameState = GameState.Loading;
  private previousStateBeforePause: GameState = GameState.Diving;

  private readonly scene: Scene;
  private readonly camera: PerspectiveCamera;
  private readonly renderer: WebGLRenderer;
  private readonly world: WorldManager;
  private readonly input: InputManager;
  private readonly player: PlayerController;
  private readonly heldTool: HeldToolView;
  private readonly combat: CombatSystem;
  private readonly mining: MiningSystem;
  private readonly inventoryUI: InventoryUI;
  private readonly shopUI: ShopUI;
  private readonly settingsUI: SettingsUI;
  private readonly touchControls: TouchControls;
  private readonly pauseMenu: PauseMenu;
  private readonly deathScreen: DeathScreen;
  private readonly minimap: MinimapUI;
  private readonly devMenu: DevMenu;
  private readonly lootDrops: LootDropManager;
  private readonly enemies: EnemyManager;
  private primaryWasHeld = false;

  private graphicsSettings: GraphicsSettings;
  private graphics: ResolvedGraphics;
  private readonly sunLight: DirectionalLight;
  private readonly fillLight: DirectionalLight;
  private readonly lampLight: PointLight;
  private readonly rimLight: PointLight;

  private readonly deathMarkers: DeathMarkerEntity[] = [];
  private readonly renderPos = vec3();
  private readonly eyePos = vec3();
  private readonly aimDir = vec3();
  private readonly lockedTouchAimDir = vec3();
  private hasLockedTouchAim = false;
  private readonly moveBroadcastIntervalMs = 50;
  private lastMoveBroadcastAt = 0;
  private lastActionAt = 0;
  private doorCooldownUntil = 0;
  private lastRenderTime = 0;
  private lastMineFailToastAt = 0;
  private nearExitDoor = false;
  private exitDoorArmed = false;
  private exitAdvanceConsumed = false;
  private nearShop = false;
  /** Soft-lock exit while a boss fight is active. */
  private bossExitLocked = false;

  private accumulator = 0;
  private lastFrameTime = 0;
  private rafId = 0;
  private running = false;
  private readonly fixedDt: number;

  private multiplayer: MultiplayerSession | null = null;
  private remotes: RemotePlayerManager | null = null;
  private lastSnapshotSaveAt = 0;
  private readonly snapshotSaveIntervalMs = 5000;
  private lastPlayerSaveAt = 0;
  private readonly playerSaveIntervalMs = 4000;
  private applyingRemoteBlock = false;
  private lastEnemyBroadcastAt = 0;
  private readonly enemyBroadcastIntervalMs = 50;
  private enemyStateSequence = 0;
  /** Remaining camera shake intensity after taking a hit (seconds-ish decay). */
  private hurtShake = 0;

  private boundVisibility: () => void;
  private boundPointerLockChange: () => void;
  private boundPageHide: () => void;

  constructor(options: EngineOptions) {
    this.config = Object.freeze({ ...DEFAULT_GAME_CONFIG, ...options.config });
    this.fixedDt = 1 / this.config.fixedTickRate;

    const playerId = options.playerId ?? `local-${cryptoRandomId()}`;
    const displayName = options.displayName ?? 'Miner';
    const saved = loadPlayerSave(playerId, displayName);
    this.profile = saved
      ? profileFromSave(saved, playerId, displayName)
      : createDefaultProfile(playerId, displayName);
    this.profile.displayName = displayName;
    this.runInventory = saved ? { ...saved.run } : createEmptyRunInventory();
    this.network = new NetworkBridge(playerId);
    this.inventory = new InventoryManager();
    if (saved) {
      this.inventory.hydrateFromSave({
        bank: saved.bank,
        ownedGear: saved.ownedGear,
        hotbar: saved.hotbar,
        runBag: saved.runBag,
      });
      this.inventory.syncProfile(this.profile);
    } else {
      this.inventory.hydrateFromProfile(this.profile);
    }

    this.crafting = new CraftingService(
      this.inventory,
      () => this.profile.walletCredits,
      (amount) => {
        if (this.profile.walletCredits < amount) return false;
        this.profile.walletCredits -= amount;
        return true;
      },
    );

    this.scene = new Scene();
    this.scene.background = new Color(0x1a2430);
    this.scene.fog = new Fog(0x1a2430, 18, 52);

    const aspect = window.innerWidth / Math.max(1, window.innerHeight);
    this.camera = new PerspectiveCamera(75, aspect, 0.08, 256);
    this.camera.position.set(0, 2, 0);
    this.scene.add(this.camera);

    this.graphicsSettings = loadGraphicsSettings();
    this.graphics = resolveGraphics(this.graphicsSettings);

    this.renderer = new WebGLRenderer({
      antialias: false,
      powerPreference: 'high-performance',
      // Avoid stencil on weak GPUs; depth is enough for this scene.
      stencil: false,
    });
    this.applyPixelRatio();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    options.canvasParent.appendChild(this.renderer.domElement);

    const lights = this.installLights();
    this.sunLight = lights.sun;
    this.fillLight = lights.fill;
    this.lampLight = lights.lamp;
    this.rimLight = lights.rim;

    this.world = new WorldManager(this.scene, this.config);
    this.applyGraphics(this.graphics, false);
    this.input = new InputManager(this.renderer.domElement);
    this.player = new PlayerController(
      playerId,
      this.world,
      this.config,
      { x: 8, y: 4, z: 8 },
      this.input,
    );
    this.heldTool = new HeldToolView(this.camera);
    this.combat = new CombatSystem(this.scene, this.world);
    this.mining = new MiningSystem(this.scene, this.world);
    this.lootDrops = new LootDropManager(this.scene);
    this.enemies = new EnemyManager(this.scene, this.world, this.config.blockSize);
    this.combat.setEnemyManager(this.enemies);
    this.syncHeldTool();

    const pointerLock = {
      requestPointerLock: () => this.input.requestPointerLock(),
      exitPointerLock: () => this.input.exitPointerLock(),
    };

    this.inventoryUI = new InventoryUI(options.canvasParent, {
      inventory: this.inventory,
      crafting: this.crafting,
      getGameState: () => this.state,
      getWallet: () => this.profile.walletCredits,
      onEquipChanged: () => {
        this.syncHeldTool();
        this.inventory.syncProfile(this.profile);
        this.events.emit('inventory:changed', { source: 'equip' });
      },
      onCrafted: (recipeId, resultItemId) => {
        this.events.emit('craft:completed', { recipeId, resultItemId });
        this.events.emit('inventory:changed', { source: 'craft' });
        this.syncHeldTool();
        this.inventory.syncProfile(this.profile);
      },
      ...pointerLock,
    });

    this.shopUI = new ShopUI(options.canvasParent, {
      inventory: this.inventory,
      getGameState: () => this.state,
      getWallet: () => this.profile.walletCredits,
      sellRunItem: (itemId, amount) => this.sellRunItem(itemId, amount),
      ...pointerLock,
    });

    this.settingsUI = new SettingsUI(options.canvasParent, {
      input: this.input,
      getGraphicsQuality: () => this.graphicsSettings.quality,
      setGraphicsQuality: (quality) => this.setGraphicsQuality(quality),
      ...pointerLock,
      onClosed: () => {
        // Resume() clears Paused before closing settings so we don't re-open pause.
        if (this.state === GameState.Paused && !this.pauseMenu.isOpen) {
          this.pauseMenu.show({
            onResume: () => this.resume(),
            onControls: () => this.openControls(),
          });
        } else if (this.state === GameState.Diving && this.player.state.alive) {
          this.input.requestPointerLock();
        }
        this.syncCaptureBlocked();
      },
    });

    this.pauseMenu = new PauseMenu(options.canvasParent);
    this.minimap = new MinimapUI(options.canvasParent);
    this.deathScreen = new DeathScreen(options.canvasParent);
    this.devMenu = new DevMenu(options.canvasParent, {
      getGameState: () => this.state,
      getCurrentStage: () => this.runInventory.currentLevel,
      skipToStage: (stage) => this.skipToStage(stage),
      ...pointerLock,
    });
    this.touchControls = new TouchControls(options.canvasParent, this.input, {
      onForge: () => this.openForgeFromTouch(),
      onShop: () => this.openShopFromTouch(),
      onPause: () => {
        if (this.state === GameState.Diving) {
          this.input.pulseTouchAction('pause');
        }
      },
    });

    this.boundVisibility = () => {
      if (document.hidden && this.running) {
        this.persistPlayerSave(true);
        this.lastFrameTime = performance.now();
        this.accumulator = 0;
      }
    };
    this.boundPointerLockChange = () => {
      if (document.pointerLockElement !== this.renderer.domElement) {
        this.mining.setMining(false);
        this.primaryWasHeld = false;
      }
    };
    this.boundPageHide = () => {
      if (this.running) this.persistPlayerSave(true);
    };

    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.boundVisibility);
    document.addEventListener('pointerlockchange', this.boundPointerLockChange);
    window.addEventListener('pagehide', this.boundPageHide);

    this.inventory.onChange(() => {
      this.syncRunCounters();
      this.inventoryUI.refresh();
      if (this.shopUI.isOpen) this.shopUI.refresh();
      this.persistPlayerSave(false);
    });

    this.wireNetwork();
  }

  getInput(): InputManager {
    return this.input;
  }

  openControls(): void {
    if (this.pauseMenu.isOpen) this.pauseMenu.hide();
    if (this.inventoryUI.isOpen) this.inventoryUI.setOpen(false);
    if (this.shopUI.isOpen) this.shopUI.setOpen(false);
    this.mining.setMining(false);
    this.settingsUI.setOpen(true);
    this.syncCaptureBlocked();
  }

  /** Instant forge open from the on-screen Forge button (single tap). */
  openForgeFromTouch(): void {
    if (this.state !== GameState.Diving) return;
    if (this.deathScreen.isVisible || this.settingsUI.isOpen || this.devMenu.isOpen) return;
    if (this.shopUI.isOpen) this.shopUI.setOpen(false);
    this.mining.setMining(false);
    this.primaryWasHeld = false;
    this.inventoryUI.toggle();
    this.syncCaptureBlocked();
  }

  /** Instant shop open from the on-screen Shop button (single tap, near shop only). */
  openShopFromTouch(): void {
    if (this.state !== GameState.Diving) return;
    if (!this.nearShop) return;
    if (this.deathScreen.isVisible || this.settingsUI.isOpen || this.devMenu.isOpen) return;
    if (this.inventoryUI.isOpen) this.inventoryUI.setOpen(false);
    this.mining.setMining(false);
    this.primaryWasHeld = false;
    if (!this.shopUI.isOpen) this.shopUI.setOpen(true);
    this.syncCaptureBlocked();
  }

  get gameState(): GameState {
    return this.state;
  }

  get activeInventory(): Readonly<ActiveRunInventory> {
    return this.runInventory;
  }

  getWorld(): WorldManager {
    return this.world;
  }

  getPlayer(): PlayerController {
    return this.player;
  }

  getDeathMarkers(): ReadonlyArray<DeathMarkerEntity> {
    return this.deathMarkers;
  }

  getHeldToolName(): string {
    return this.heldTool.displayName;
  }

  getPlayerHealth(): { health: number; maxHealth: number } {
    return { health: this.player.state.health, maxHealth: this.player.state.maxHealth };
  }

  getEnemyCount(): number {
    return this.enemies.aliveCount();
  }

  isInventoryOpen(): boolean {
    return this.inventoryUI.isOpen;
  }

  isDevMenuOpen(): boolean {
    return this.devMenu.isOpen;
  }

  /**
   * Dev helper: jump to an endless stage (regens the cave column when needed).
   * Host / offline only — clients follow via stage:enter.
   */
  skipToStage(stage: number): boolean {
    const target = Math.floor(stage);
    if (!Number.isFinite(target) || target < 1) return false;
    if (this.multiplayer?.role === 'client') return false;
    if (
      this.state === GameState.Loading ||
      this.state === GameState.Respawning ||
      this.deathScreen.isVisible
    ) {
      return false;
    }

    if (this.shopUI.isOpen) this.shopUI.setOpen(false);
    if (this.inventoryUI.isOpen) this.inventoryUI.setOpen(false);

    const n = this.world.getLevelCount();
    const columnStart = target - (((target - 1) % n) + 1) + 1;
    const slot = this.physicalSlotForStage(target);
    const level = this.world.getLevel(slot);
    const expectedBoss = isBossStage(target) ? bossKindForStage(target) : null;
    const columnMismatch = columnStart !== this.world.getColumnStartStage();
    const bossMismatch = Boolean(expectedBoss && level?.bossKind !== expectedBoss);

    if (columnMismatch || bossMismatch) {
      this.lootDrops.clearAll();
      this.mining.clearAll();
      this.enemies.clearAll();
      const seed = this.world.regenerate(
        columnMismatch ? undefined : this.world.getSeed(),
        columnStart,
      );
      this.events.emit('world:ready', {
        width: this.config.worldWidth,
        height: this.config.worldHeight,
        depth: this.config.worldDepth,
        levelCount: n,
        seed,
      });
    }

    this.enterStage(target, true);
    if (this.multiplayer?.role === 'host') {
      this.broadcastStageEnter();
      this.persistSnapshot(true);
    }
    this.events.emit('ui:toast', {
      message: `Dev skip → stage ${target}`,
      kind: 'info',
    });
    return true;
  }

  isShopOpen(): boolean {
    return this.shopUI.isOpen;
  }

  isNearShop(): boolean {
    return this.nearShop;
  }

  get multiplayerRole(): MultiplayerRole {
    return this.multiplayer?.role ?? null;
  }

  get roomCode(): string | null {
    return this.multiplayer?.code ?? null;
  }

  /** Generate world and drop straight into stage 1 — or resume a saved singleplayer run. */
  start(options: { continueSave?: boolean } = {}): void {
    if (this.running) return;

    this.setState(GameState.Loading);
    saveLastSessionHint({ mode: 'singleplayer' });

    const saved = loadPlayerSave(this.profile.playerId, this.profile.displayName);
    const canContinue =
      options.continueSave !== false && hasContinuableSingleplayerSave(saved);
    const resume = canContinue ? saved!.singleplayer! : null;

    if (resume) {
      const n = this.config.levelCount;
      const stage = Math.max(1, resume.stage);
      const columnStart = stage - (((stage - 1) % n) + 1) + 1;
      const seed = this.world.regenerate(resume.seed, columnStart);
      if (resume.runId > 0) {
        this.world.setRunId(resume.runId);
      }
      this.world.applyDigs(resume.digs ?? []);

      // Restore the SP dive bag (online sessions must not clobber it).
      this.inventory.clearRunBag();
      if (resume.runBag?.length) {
        this.inventory.addRunSlots(resume.runBag);
      } else if (saved?.runBag?.length && !saved.singleplayer?.runBag) {
        // Back-compat with older saves that only stored top-level runBag.
        this.inventory.addRunSlots(saved.runBag);
      }
      this.runInventory = resume.run
        ? { ...resume.run, currentLevel: stage }
        : { ...createEmptyRunInventory(), currentLevel: stage };
      this.syncRunCounters();

      this.events.emit('world:ready', {
        width: this.config.worldWidth,
        height: this.config.worldHeight,
        depth: this.config.worldDepth,
        levelCount: this.config.levelCount,
        seed,
      });

      this.network.connect();
      this.running = true;
      this.lastFrameTime = performance.now();
      this.accumulator = 0;
      this.enterStage(stage, true);
      if (resume.position) {
        this.player.state.position.x = resume.position.x;
        this.player.state.position.y = resume.position.y;
        this.player.state.position.z = resume.position.z;
        this.player.state.velocity.x = 0;
        this.player.state.velocity.y = 0;
        this.player.state.velocity.z = 0;
      }
      this.events.emit('ui:toast', {
        message: `Continued — stage ${stage}`,
        kind: 'info',
      });
    } else {
      // Fresh dive: keep wallet/bank/gear, clear run bag + world digs.
      this.inventory.clearRunBag();
      this.runInventory = createEmptyRunInventory();
      this.syncRunCounters();

      const seed = this.world.regenerate(undefined, 1);
      this.events.emit('world:ready', {
        width: this.config.worldWidth,
        height: this.config.worldHeight,
        depth: this.config.worldDepth,
        levelCount: this.config.levelCount,
        seed,
      });

      this.network.connect();
      this.running = true;
      this.lastFrameTime = performance.now();
      this.accumulator = 0;
      this.enterStage(1, true);
    }

    this.persistPlayerSave(true);
    this.rafId = requestAnimationFrame(this.frame);
  }

  /** Host or join an online session (host-authoritative world). */
  startMultiplayer(session: MultiplayerSession): void {
    if (this.running) return;

    this.multiplayer = session;
    this.profile.displayName = session.displayName;
    this.remotes = new RemotePlayerManager(this.scene, session.playerId);
    for (const peer of session.players) {
      this.remotes.upsert(peer.playerId, peer.displayName);
    }

    this.network.attachSocket(session.socket);
    this.wireMultiplayerSession(session);
    this.combat.setReportOnly(session.role === 'client');

    saveLastSessionHint({
      mode: session.role === 'host' ? 'host' : 'join',
      code: session.code,
    });

    this.setState(GameState.Loading);
    const snap = session.snapshot;
    const stage = Math.max(1, snap.stage);
    const n = this.config.levelCount;
    const columnStart = stage - (((stage - 1) % n) + 1) + 1;
    const seed = this.world.regenerate(snap.seed, columnStart);
    // regenerate bumps runId; keep a saved runId when resuming, otherwise keep the new one.
    if (snap.runId > 0) {
      this.world.setRunId(snap.runId);
    }
    this.world.applyDigs(snap.digs ?? []);

    this.events.emit('world:ready', {
      width: this.config.worldWidth,
      height: this.config.worldHeight,
      depth: this.config.worldDepth,
      levelCount: this.config.levelCount,
      seed,
    });
    this.events.emit('network:session', { code: session.code, role: session.role });

    this.network.connect();
    this.running = true;
    this.lastFrameTime = performance.now();
    this.accumulator = 0;
    this.enterStage(Math.max(1, snap.stage), true);

    if (session.role === 'host') {
      this.persistSnapshot(true);
      this.broadcastStageEnter();
    }

    session.socket.emit('peer:hello', {
      playerId: session.playerId,
      displayName: session.displayName,
      role: session.role,
    });

    this.persistPlayerSave(true);
    this.rafId = requestAnimationFrame(this.frame);
  }

  stop(): void {
    if (!this.running) return;
    this.persistPlayerSave(true);
    if (this.multiplayer?.role === 'host') {
      this.persistSnapshot(true);
    }
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  dispose(): void {
    this.stop();
    this.network.disconnect();
    this.remotes?.dispose();
    this.remotes = null;
    this.multiplayer = null;
    this.player.dispose();
    this.input.dispose();
    this.heldTool.dispose();
    this.combat.dispose();
    this.mining.dispose();
    this.lootDrops.dispose();
    this.enemies.dispose();
    this.inventoryUI.dispose();
    this.shopUI.dispose();
    this.settingsUI.dispose();
    this.pauseMenu.dispose();
    this.deathScreen.dispose();
    this.minimap.dispose();
    this.devMenu.dispose();
    this.touchControls.dispose();
    this.world.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.boundVisibility);
    document.removeEventListener('pointerlockchange', this.boundPointerLockChange);
    window.removeEventListener('pagehide', this.boundPageHide);
    this.events.clear();
  }

  pause(): void {
    if (
      this.state === GameState.Paused ||
      this.state === GameState.Loading ||
      this.state === GameState.Respawning
    ) {
      return;
    }
    if (this.inventoryUI.isOpen) this.inventoryUI.setOpen(false);
    if (this.shopUI.isOpen) this.shopUI.setOpen(false);
    if (this.settingsUI.isOpen) this.settingsUI.setOpen(false);
    if (this.devMenu.isOpen) this.devMenu.setOpen(false);
    this.mining.setMining(false);
    this.primaryWasHeld = false;
    this.previousStateBeforePause = this.state;
    this.setState(GameState.Paused);
    this.input.exitPointerLock();
    this.pauseMenu.show({
      onResume: () => this.resume(),
      onControls: () => this.openControls(),
    });
    this.syncCaptureBlocked();
  }

  resume(): void {
    if (this.state !== GameState.Paused) return;
    this.pauseMenu.hide();
    const resumeState = this.previousStateBeforePause;
    this.setState(resumeState);
    if (this.settingsUI.isOpen) this.settingsUI.setOpen(false);
    this.lastFrameTime = performance.now();
    this.accumulator = 0;
    this.syncCaptureBlocked();
    if (resumeState === GameState.Diving && this.player.state.alive) {
      this.input.requestPointerLock();
    }
  }

  modifyBlock(x: number, y: number, z: number, newType: BlockType): BlockType | null {
    const current = this.world.getBlock(x, y, z);
    if (current === BlockType.Bedrock) return current;
    if (
      current === BlockType.ExitDoor ||
      current === BlockType.SurfacePortal ||
      current === BlockType.Shop
    ) {
      return current;
    }

    const previous = this.world.modifyBlock(x, y, z, newType);
    if (previous === null || previous === newType) return previous;

    this.events.emit('block:changed', { x, y, z, previous, next: newType });
    this.minimap.invalidate();
    if (!this.applyingRemoteBlock) {
      this.network.broadcastBlockChange(x, y, z, newType);
      if (this.multiplayer?.role === 'host') {
        this.persistSnapshot(false);
      }
      if (!this.multiplayer) {
        this.persistPlayerSave(false);
      }
    }

    if (previous !== BlockType.Air && newType === BlockType.Air && !this.applyingRemoteBlock) {
      this.collectBlock(previous);
      this.events.emit('block:mined', { x, y, z, blockType: previous });
    }
    return previous;
  }

  // ---------------------------------------------------------------------------
  // Frame loop
  // ---------------------------------------------------------------------------

  private readonly frame = (now: number): void => {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.frame);

    let frameTime = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    if (frameTime > 0.25) frameTime = 0.25;

    this.syncCaptureBlocked();
    this.input.update(frameTime);
    this.processActions();

    if (
      this.state === GameState.Paused ||
      this.state === GameState.Loading ||
      this.state === GameState.Respawning ||
      this.inventoryUI.isOpen ||
      this.shopUI.isOpen ||
      this.settingsUI.isOpen ||
      this.devMenu.isOpen
    ) {
      // Keep look angles mirrored for HUD / camera while menus are open.
      this.player.input.yaw = this.input.getYaw();
      this.player.input.pitch = this.input.getPitch();
      this.render(1);
      return;
    }

    this.accumulator += frameTime;
    const maxSteps = this.config.maxFrameSteps;
    let steps = 0;

    while (this.accumulator >= this.fixedDt && steps < maxSteps) {
      this.fixedUpdate(this.fixedDt);
      this.accumulator -= this.fixedDt;
      steps++;
    }
    if (steps === maxSteps) {
      this.accumulator = 0;
    }

    const alpha = this.accumulator / this.fixedDt;
    this.render(alpha);
    if (!this.multiplayer) {
      this.persistPlayerSave(false);
    }
  };

  private fixedUpdate(dt: number): void {
    if (this.state === GameState.Respawning) {
      return;
    }

    if (this.state !== GameState.Diving) {
      return;
    }

    this.player.beginTick();
    const impact = this.player.fixedUpdate(dt);

    const isClient = this.multiplayer?.role === 'client';
    const isHost = this.multiplayer?.role === 'host';

    // Clients still fire local projectiles for juice, but enemy HP is host-authoritative.
    const projectileHits = this.combat.update(dt);
    for (const hit of projectileHits) {
      if (isClient) {
        this.sendEnemyHitIntent(hit.enemyId, hit.damage, this.lookKnockDir());
        continue;
      }
      if (hit.killed) {
        this.events.emit('enemy:killed', { enemyId: hit.enemyId, name: hit.enemyName });
        this.onBossTargetMaybeCleared(hit.enemyName);
      }
    }

    this.updateMining(dt);
    this.lootDrops.update(dt);
    this.tryRecoverLoot();

    if (isClient) {
      this.enemies.updateVisuals(dt);
    } else {
      const enemyResult = this.enemies.update(
        dt,
        this.player.state.position,
        this.player.state.alive,
        {
          localPlayerId: this.profile.playerId,
          extraTargets: this.remotes?.listTargets() ?? [],
        },
      );

      if (isHost) {
        for (const hit of enemyResult.contactHits) {
          if (hit.targetId === this.profile.playerId) continue;
          this.multiplayer!.socket.emit('player:hurt', {
            playerId: hit.targetId,
            amount: hit.amount,
            sourceEnemyId: hit.sourceEnemyId,
          } satisfies PlayerHurtPacket);
        }
      }

      if (enemyResult.playerDamage > 0 && this.player.state.alive) {
        let sourceEnemyId: string | undefined;
        for (const hit of enemyResult.contactHits) {
          if (hit.targetId === this.profile.playerId) sourceEnemyId = hit.sourceEnemyId;
        }
        const killed = this.applyPlayerHurt(enemyResult.playerDamage, sourceEnemyId);
        if (killed) {
          this.mining.setMining(false);
          this.handlePlayerDeath('combat', 0);
          return;
        }
      }

      if (isHost) {
        const now = performance.now();
        if (now - this.lastEnemyBroadcastAt >= this.enemyBroadcastIntervalMs) {
          this.lastEnemyBroadcastAt = now;
          this.broadcastEnemyState();
        }
      }
    }

    this.syncBossExitLock();
    this.trackDepth();
    this.checkDoors();

    if (impact.died) {
      this.mining.setMining(false);
      this.handlePlayerDeath('fall', impact.impactSpeed);
      return;
    }

    const t = performance.now();
    if (t - this.lastMoveBroadcastAt >= this.moveBroadcastIntervalMs) {
      this.lastMoveBroadcastAt = t;
      this.network.broadcastPlayerMove(
        this.player.state.position,
        this.player.state.velocity,
        this.player.input.yaw,
        this.player.state.health,
        this.player.state.maxHealth,
      );
    }
  }

  private render(alpha: number): void {
    const now = performance.now();
    const dt = this.lastRenderTime === 0 ? 0 : Math.min(0.05, (now - this.lastRenderTime) / 1000);
    this.lastRenderTime = now;

    this.player.getRenderPosition(alpha, this.renderPos);
    const eyeY = this.renderPos.y + this.config.playerHalfExtents.y * 0.65;
    this.camera.position.set(this.renderPos.x, eyeY, this.renderPos.z);
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.player.input.yaw;
    this.camera.rotation.x = this.player.input.pitch;

    if (this.hurtShake > 0) {
      this.hurtShake = Math.max(0, this.hurtShake - dt * 2.8);
      const mag = this.hurtShake * this.hurtShake * 0.22;
      this.camera.position.x += (Math.random() - 0.5) * mag;
      this.camera.position.y += (Math.random() - 0.5) * mag * 0.55;
      this.camera.position.z += (Math.random() - 0.5) * mag;
    }

    const speed = Math.hypot(this.player.state.velocity.x, this.player.state.velocity.z);
    const moving = Math.min(1, speed / this.config.maxHorizontalSpeed);
    this.heldTool.update(dt, this.player.state.onGround ? moving : moving * 0.35);
    this.world.updateDecor(dt);
    this.remotes?.update(dt);
    this.updateMinimap();

    this.renderer.render(this.scene, this.camera);
  }

  private updateMinimap(): void {
    const stage = Math.max(1, this.runInventory.currentLevel);
    const slot = this.physicalSlotForStage(stage);
    const level = this.world.getLevel(slot);
    const markers: MinimapMarker[] = [];

    if (level) {
      markers.push({
        x: level.entrance.x,
        z: level.entrance.z,
        kind: 'entrance',
      });
      markers.push({
        x: (level.exitDoor.x + 0.5) * this.config.blockSize,
        z: (level.exitDoor.z + 0.5) * this.config.blockSize,
        kind: 'exit',
      });
      markers.push({
        x: (level.shop.x + 0.5) * this.config.blockSize,
        z: (level.shop.z + 0.5) * this.config.blockSize,
        kind: 'shop',
      });
    }

    for (const peer of this.remotes?.listTargets() ?? []) {
      markers.push({
        x: peer.position.x,
        z: peer.position.z,
        kind: 'remote',
      });
    }

    for (const enemy of this.enemies.list()) {
      if (!enemy.alive) continue;
      markers.push({
        x: enemy.position.x,
        z: enemy.position.z,
        kind: 'enemy',
      });
    }

    this.minimap.render(
      this.world,
      {
        stage,
        level,
        player: this.player.state.position,
        yaw: this.player.input.yaw,
        markers,
        revealRadius: 8,
      },
      this.graphics.minimapHz,
    );
  }

  // ---------------------------------------------------------------------------
  // Actions / combat / mining
  // ---------------------------------------------------------------------------

  private syncCaptureBlocked(): void {
    const blocked =
      this.state === GameState.Paused ||
      this.state === GameState.Loading ||
      this.state === GameState.Respawning ||
      this.inventoryUI.isOpen ||
      this.shopUI.isOpen ||
      this.settingsUI.isOpen ||
      this.devMenu.isOpen ||
      this.deathScreen.isVisible;
    this.input.setCaptureBlocked(blocked);
    this.touchControls.setVisible(!blocked && this.state === GameState.Diving);
  }

  private processActions(): void {
    if (this.input.isRebinding()) return;

    // Death confirm (DeathScreen also listens for Enter/Space click path)
    if (this.deathScreen.isVisible) {
      if (
        this.input.consumePressed('confirm') ||
        this.input.consumePressed('jump')
      ) {
        this.deathScreen.confirm();
      }
      return;
    }

    // Backtick — toggle dev menu (works even over other overlays).
    if (this.input.consumeKey('Backquote')) {
      if (this.state !== GameState.Paused) {
        if (this.shopUI.isOpen) this.shopUI.setOpen(false);
        if (this.inventoryUI.isOpen) this.inventoryUI.setOpen(false);
        if (this.settingsUI.isOpen) this.settingsUI.setOpen(false);
        this.mining.setMining(false);
        this.primaryWasHeld = false;
        this.devMenu.toggle();
        this.syncCaptureBlocked();
      }
      return;
    }

    if (this.devMenu.isOpen) {
      if (this.input.consumePressed('pause')) {
        this.devMenu.setOpen(false);
        this.syncCaptureBlocked();
      }
      return;
    }

    if (this.settingsUI.isOpen) {
      if (this.input.consumePressed('pause')) {
        this.settingsUI.setOpen(false);
        this.syncCaptureBlocked();
      }
      return;
    }

    if (this.state === GameState.Paused) {
      if (this.input.consumePressed('pause')) {
        this.resume();
      }
      return;
    }

    if (this.input.consumePressed('inventory')) {
      if (this.shopUI.isOpen) this.shopUI.setOpen(false);
      this.mining.setMining(false);
      this.primaryWasHeld = false;
      this.inventoryUI.toggle();
      this.syncCaptureBlocked();
      return;
    }

    if (this.shopUI.isOpen) {
      if (this.input.consumePressed('pause') || this.input.consumePressed('interact')) {
        this.shopUI.setOpen(false);
        this.syncCaptureBlocked();
      }
      return;
    }

    if (this.inventoryUI.isOpen) {
      if (this.input.consumePressed('pause')) {
        this.inventoryUI.setOpen(false);
        this.syncCaptureBlocked();
      }
      return;
    }

    if (this.input.consumePressed('interact')) {
      if (this.nearShop && this.state === GameState.Diving) {
        this.mining.setMining(false);
        this.primaryWasHeld = false;
        this.shopUI.toggle();
        this.syncCaptureBlocked();
        return;
      }
    }

    const hotbarActions = [
      'hotbar1',
      'hotbar2',
      'hotbar3',
      'hotbar4',
      'hotbar5',
    ] as const;
    for (let i = 0; i < hotbarActions.length; i++) {
      if (this.input.consumePressed(hotbarActions[i]!)) {
        if (this.inventory.setActiveHotbar(i)) {
          this.syncHeldTool();
          this.inventory.syncProfile(this.profile);
          this.events.emit('inventory:changed', { source: 'equip' });
        }
        break;
      }
    }

    if (this.input.consumePressed('pause')) {
      this.pause();
      return;
    }

    this.updatePrimaryAction();
  }

  private updatePrimaryAction(): void {
    const held = this.input.isHeld('primary');
    const justPressed = held && !this.primaryWasHeld;
    const justReleased = !held && this.primaryWasHeld;
    this.primaryWasHeld = held;

    if (justReleased) {
      this.mining.setMining(false);
      this.input.clearTouchAimPoint();
      this.hasLockedTouchAim = false;
    }

    if (!justPressed) return;
    if (
      this.inventoryUI.isOpen ||
      this.shopUI.isOpen ||
      this.settingsUI.isOpen ||
      this.devMenu.isOpen
    ) {
      return;
    }
    // Mouse primary needs pointer lock; gamepad/touch can attack/mine without it.
    if (
      !this.input.isUsingGamepad() &&
      !this.input.isUsingTouch() &&
      !this.input.isPointerLocked()
    ) {
      return;
    }
    if (this.state !== GameState.Diving) return;
    if (!this.player.state.alive) return;

    const itemId = this.inventory.getActiveItemId();
    if (!itemId) return;

    const def = ITEM_CATALOG[itemId];
    this.getEyePosition(this.eyePos);
    this.fillAimDirection(this.aimDir);

    // Tools: strike enemies on press, otherwise hold-to-mine.
    if (def.category === 'tool' && def.minePower > 0) {
      if (this.combat.hasEnemyInReach(itemId, this.eyePos, this.aimDir)) {
        this.tryCombatSwing(itemId, def.cooldownMs);
        return;
      }
      this.heldTool.playSwing();
      this.mining.setMining(true);
      return;
    }

    // Weapons: press-to-swing / fire.
    this.tryCombatSwing(itemId, def.cooldownMs);
  }

  private tryCombatSwing(itemId: ItemId, cooldownMs: number): void {
    const cooldown = cooldownMs > 0 ? cooldownMs : this.config.mineCooldownMs;
    const now = performance.now();
    if (now - this.lastActionAt < cooldown) return;
    this.lastActionAt = now;

    this.getEyePosition(this.eyePos);
    this.fillAimDirection(this.aimDir);
    this.heldTool.playSwing();
    const hit = this.combat.fire(itemId, this.eyePos, this.aimDir);

    if (hit.kind === 'entity' && hit.enemyId) {
      if (this.multiplayer?.role === 'client') {
        this.sendEnemyHitIntent(hit.enemyId, hit.damage, this.lookKnockDir());
        return;
      }
      if (hit.enemyKilled) {
        this.events.emit('enemy:killed', {
          enemyId: hit.enemyId ?? 'unknown',
          name: hit.enemyName ?? 'enemy',
        });
        this.onBossTargetMaybeCleared(hit.enemyName ?? 'enemy');
      }
      return;
    }

    if (
      hit.kind === 'block' &&
      hit.blockBroken &&
      hit.x !== undefined &&
      hit.y !== undefined &&
      hit.z !== undefined
    ) {
      this.modifyBlock(hit.x, hit.y, hit.z, BlockType.Air);
    }
  }

  private updateMining(dt: number): void {
    if (!this.mining.isMining) return;

    this.getEyePosition(this.eyePos);
    this.fillAimDirection(this.aimDir);
    this.mining.update(
      dt,
      this.eyePos,
      this.aimDir,
      this.inventory.getActiveItemId(),
      () => this.heldTool.playSwing(),
      (x, y, z) => {
        this.modifyBlock(x, y, z, BlockType.Air);
      },
      (type) => {
        const now = performance.now();
        if (now - this.lastMineFailToastAt > 900) {
          this.lastMineFailToastAt = now;
          const need =
            type === BlockType.Gold
              ? 'Iron Pickaxe (or better)'
              : type === BlockType.Iron
                ? 'Rusty Pickaxe'
                : 'a stronger tool';
          this.events.emit('ui:toast', {
            message: `Too hard — need ${need}.`,
            kind: 'warn',
          });
        }
      },
    );
  }

  getMiningFraction(): number {
    return this.mining.target?.fraction ?? 0;
  }

  private getEyePosition(out: Vector3D): Vector3D {
    const p = this.player.state.position;
    out.x = p.x;
    out.y = p.y + this.config.playerHalfExtents.y * 0.65;
    out.z = p.z;
    return out;
  }

  /**
   * Aim ray for combat/mining: touch uses the tap/hold screen point (locked
   * for the gesture); mouse/gamepad use camera yaw/pitch (crosshair).
   */
  private fillAimDirection(out: Vector3D): void {
    const point = this.input.getTouchAimPoint();
    if (point) {
      if (!this.hasLockedTouchAim) {
        this.syncCameraAimPose();
        screenPointToWorldDirection(
          this.camera,
          this.renderer.domElement,
          point.x,
          point.y,
          this.lockedTouchAimDir,
        );
        this.hasLockedTouchAim = true;
      }
      out.x = this.lockedTouchAimDir.x;
      out.y = this.lockedTouchAimDir.y;
      out.z = this.lockedTouchAimDir.z;
      return;
    }
    this.hasLockedTouchAim = false;
    lookDirectionFromAngles(this.player.input.yaw, this.player.input.pitch, out);
  }

  /** Align the camera with the sim eye so screen→world rays match what the player sees. */
  private syncCameraAimPose(): void {
    this.getEyePosition(this.eyePos);
    this.camera.position.set(this.eyePos.x, this.eyePos.y, this.eyePos.z);
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.player.input.yaw;
    this.camera.rotation.x = this.player.input.pitch;
    this.camera.updateMatrixWorld(true);
  }

  private syncHeldTool(): void {
    this.heldTool.setItem(this.inventory.getActiveItemId());
  }

  // ---------------------------------------------------------------------------
  // Spelunky-style doors
  // ---------------------------------------------------------------------------

  private checkDoors(): void {
    const now = performance.now();
    this.nearExitDoor = false;
    this.nearShop = false;

    const feet = this.player.state.position;
    const voxel = this.world.worldToVoxel(feet.x, feet.y, feet.z);
    const chest = this.world.worldToVoxel(
      feet.x,
      feet.y + this.config.playerHalfExtents.y * 0.4,
      feet.z,
    );

    for (const v of [voxel, chest]) {
      const block = this.world.getBlock(v.x, v.y, v.z);
      if (block === BlockType.ExitDoor && this.runInventory.currentLevel >= 1) {
        this.nearExitDoor = true;
      }
      if (block === BlockType.Shop && this.runInventory.currentLevel >= 1) {
        this.nearShop = true;
      }
    }

    if (!this.nearShop && this.shopUI.isOpen) {
      this.shopUI.setOpen(false);
      this.syncCaptureBlocked();
    }

    this.touchControls.setShopAvailable(this.nearShop && this.state === GameState.Diving);

    // Host advances once the whole living party is on the exit.
    // Online clients follow via stage:enter — they don't advance the shared world.
    const canAdvance =
      this.nearExitDoor && this.allLivingPeersNearExit() && !this.isBossExitLocked();
    if (!canAdvance) {
      this.exitAdvanceConsumed = false;
    } else if (
      !this.exitAdvanceConsumed &&
      this.exitDoorArmed &&
      now >= this.doorCooldownUntil &&
      this.multiplayer?.role !== 'client'
    ) {
      this.exitAdvanceConsumed = true;
      this.advanceFromExitDoor();
    }
  }

  isNearExitDoor(): boolean {
    return this.nearExitDoor;
  }

  /** Boss stages keep the exit soft-locked until the boss (and totems) are dead. */
  isBossExitLocked(): boolean {
    return isBossStage(this.runInventory.currentLevel) && !this.enemies.bossCleared();
  }

  /** True when living remotes are also on the exit (or solo / no remotes). */
  canAdvanceExitDoor(): boolean {
    return this.nearExitDoor && this.allLivingPeersNearExit() && !this.isBossExitLocked();
  }

  /** Living multiplayer peers standing on ExitDoor voxels. Solo / offline → true. */
  private allLivingPeersNearExit(): boolean {
    if (!this.multiplayer || !this.remotes) return true;
    const targets = this.remotes.listTargets().filter((t) => t.alive);
    if (targets.length === 0) return true;
    const halfY = this.config.playerHalfExtents.y;
    for (const t of targets) {
      if (!this.positionOnExitDoor(t.position.x, t.position.y, t.position.z, halfY)) {
        return false;
      }
    }
    return true;
  }

  private positionOnExitDoor(x: number, y: number, z: number, halfY: number): boolean {
    const feet = this.world.worldToVoxel(x, y, z);
    const chest = this.world.worldToVoxel(x, y + halfY * 0.4, z);
    for (const v of [feet, chest]) {
      if (this.world.getBlock(v.x, v.y, v.z) === BlockType.ExitDoor) return true;
    }
    return false;
  }

  /**
   * Map endless stage number → physical level slot in the current column.
   * Stages 1..N use slots 1..N; stage N+1 regenerates and uses slot 1 again.
   */
  private physicalSlotForStage(stage: number): number {
    const n = this.world.getLevelCount();
    return ((stage - 1) % n) + 1;
  }

  private enterStage(stage: number, fromDoor: boolean): void {
    const slot = this.physicalSlotForStage(stage);
    const spawn = this.world.getLevelSpawn(slot);
    const level = this.world.getLevel(slot);
    if (!spawn || !level) return;

    // Only mesh the sealed chamber the party is in — huge win on weak GPUs.
    this.world.setVisibleYRange(level.floorY, level.ceilingY);

    this.player.respawn(spawn);
    this.runInventory.currentLevel = stage;
    if (stage > this.profile.highestLevelReached) {
      this.profile.highestLevelReached = stage;
    }

    if (this.multiplayer?.role !== 'client') {
      const count = this.enemies.spawnForStage(
        stage,
        level,
        this.world.getRunId(),
        this.world.getSeed(),
      );
      this.events.emit('enemy:spawned', { stage, count });
      if (isBossStage(stage)) {
        const kind = bossKindForStage(stage);
        const name = ENEMY_CATALOG[kind].name;
        this.events.emit('ui:toast', {
          message: `${name} blocks the way — defeat it to unlock the exit.`,
          kind: 'warn',
        });
      }
      if (this.multiplayer?.role === 'host') {
        this.broadcastEnemySnapshot();
      }
    } else {
      // Host will push enemy:snapshot / stage:enter.enemies shortly.
      this.enemies.clearAll();
    }

    this.syncBossExitLock(true);
    this.nearExitDoor = false;
    this.exitAdvanceConsumed = false;
    this.exitDoorArmed = false;
    this.doorCooldownUntil = performance.now() + 600;
    window.setTimeout(() => {
      this.exitDoorArmed = true;
    }, 400);

    this.setState(GameState.Diving);
    this.events.emit('level:entered', { level: stage, spawnLocation: spawn, fromDoor });
    this.minimap.resetExploration();
    this.persistPlayerSave(true);
  }

  private advanceFromExitDoor(): void {
    const current = this.runInventory.currentLevel;
    if (current < 1) return;
    if (this.isBossExitLocked()) return;

    if (this.shopUI.isOpen) this.shopUI.setOpen(false);

    const next = current + 1;
    const n = this.world.getLevelCount();
    // Every N stages, roll a fresh cave column and keep going deeper.
    if ((current % n) === 0) {
      this.lootDrops.clearAll();
      this.mining.clearAll();
      this.enemies.clearAll();
      const seed = this.world.regenerate(undefined, next);
      this.events.emit('world:ready', {
        width: this.config.worldWidth,
        height: this.config.worldHeight,
        depth: this.config.worldDepth,
        levelCount: n,
        seed,
      });
      this.events.emit('ui:toast', {
        message: `Deeper caves ahead (seed ${seed.toString(16)}). Stage ${next}. Unrecovered loot from old caves is gone.`,
        kind: 'success',
      });
    }

    this.enterStage(next, true);
    if (this.multiplayer?.role === 'host') {
      this.broadcastStageEnter();
      this.persistSnapshot(true);
    }
  }

  /** Sell run-bag materials at a floor shop for wallet credits. */
  private sellRunItem(
    itemId: ItemId,
    amount: number,
  ): { ok: boolean; credits: number; reason?: string } {
    if (amount <= 0) return { ok: false, credits: 0, reason: 'Nothing to sell.' };
    const price = getSellPrice(itemId);
    if (price <= 0) return { ok: false, credits: 0, reason: 'That item has no value.' };
    if (!this.nearShop) return { ok: false, credits: 0, reason: 'Find a shop first.' };
    if (!this.inventory.trySpendFromRun(itemId, amount)) {
      return { ok: false, credits: 0, reason: 'Not enough in run bag.' };
    }
    const credits = price * amount;
    this.profile.walletCredits += credits;
    this.profile.totalExtractions += 1;
    this.syncRunCounters();
    this.events.emit('shop:sold', { itemId, amount, credits });
    this.events.emit('inventory:changed', { source: 'sell' });
    this.events.emit('ui:toast', {
      message: `Sold ${amount}× ${ITEM_CATALOG[itemId].name} for ${credits}¢`,
      kind: 'success',
    });
    return { ok: true, credits };
  }

  // ---------------------------------------------------------------------------
  // Death / respawn — stay in the caves, never return to camp
  // ---------------------------------------------------------------------------

  /** Damage + knockback + camera shake when an enemy lands a hit. */
  private applyPlayerHurt(amount: number, sourceEnemyId?: string): boolean {
    if (sourceEnemyId) {
      const source = this.enemies.list().find((e) => e.id === sourceEnemyId && e.alive);
      if (source) {
        this.player.applyKnockback(
          this.player.state.position.x - source.position.x,
          this.player.state.position.z - source.position.z,
        );
      }
    }
    const killed = this.player.takeDamage(amount);
    this.hurtShake = Math.max(this.hurtShake, 0.55);
    this.events.emit('player:damaged', {
      playerId: this.profile.playerId,
      amount,
      health: this.player.state.health,
      maxHealth: this.player.state.maxHealth,
    });
    return killed;
  }

  private handlePlayerDeath(cause: 'fall' | 'hazard' | 'combat' | 'unknown', impactSpeed: number): void {
    if (this.state === GameState.Respawning) return;

    const deathLocation: Vector3D = {
      x: this.player.state.position.x,
      y: this.player.state.position.y,
      z: this.player.state.position.z,
    };

    const stage = Math.max(1, this.runInventory.currentLevel);
    const lostLoot = this.inventory.drainRunBag();
    this.syncRunCounters();
    this.events.emit('inventory:reset', { playerId: this.profile.playerId });
    this.events.emit('inventory:changed', { source: 'death' });

    this.profile.totalDeaths += 1;

    const dropId = `death-${this.profile.totalDeaths}-${cryptoRandomId()}`;
    const drop = this.lootDrops.spawn(
      dropId,
      deathLocation,
      lostLoot,
      this.world.getRunId(),
      stage,
    );

    const marker: DeathMarkerEntity = {
      id: dropId,
      kind: cause === 'fall' ? 'gravestone' : 'drop-pod',
      position: { ...deathLocation },
      ownerPlayerId: this.profile.playerId,
      createdAt: performance.now(),
      lootPending: drop !== null,
    };
    this.deathMarkers.push(marker);
    if (this.deathMarkers.length > 32) {
      this.deathMarkers.shift();
    }

    let lootCount = 0;
    if (drop) {
      lootCount = this.lootDrops.totalItems(drop.contents);
      this.events.emit('loot:dropped', {
        dropId: drop.id,
        position: deathLocation,
        itemCount: lootCount,
        stage,
      });
    }

    this.events.emit('player:death', {
      playerId: this.profile.playerId,
      deathLocation,
      cause,
      impactSpeed,
    });

    this.network.broadcastPlayerDeath(this.profile.playerId, deathLocation);

    if (this.inventoryUI.isOpen) this.inventoryUI.setOpen(false);
    if (this.shopUI.isOpen) this.shopUI.setOpen(false);
    this.mining.setMining(false);
    if (document.pointerLockElement === this.renderer.domElement) {
      document.exitPointerLock();
    }

    this.setState(GameState.Respawning);
    this.tryResetBossesOnPartyWipe();
    this.persistPlayerSave(true);
    this.deathScreen.show(
      {
        cause,
        impactSpeed,
        stage,
        lootDropped: lootCount,
        totalDeaths: this.profile.totalDeaths,
      },
      () => this.completeRespawn(),
    );
  }

  /**
   * When every living miner is down during a boss fight, restore the boss (and totems)
   * so the next attempt starts from full HP. Host / solo owns the sim.
   */
  private tryResetBossesOnPartyWipe(): void {
    if (this.player.state.alive) return;
    if (this.multiplayer?.role === 'client') return;

    const remotes = this.remotes?.listTargets() ?? [];
    if (remotes.some((t) => t.alive)) return;

    const stage = Math.max(1, this.runInventory.currentLevel);
    if (!isBossStage(stage)) return;
    if (this.enemies.bossCleared()) return;

    if (!this.enemies.healBossesToFull()) return;

    this.events.emit('ui:toast', {
      message: 'Boss recovered — party wiped',
      kind: 'warn',
    });
    if (this.multiplayer?.role === 'host') {
      this.broadcastEnemyState();
    }
  }

  private completeRespawn(): void {
    // Respawn at the entrance of the current stage — still forward progression.
    const stage = Math.max(1, this.runInventory.currentLevel);
    const slot = this.physicalSlotForStage(stage);
    const spawn = this.world.getLevelSpawn(slot);
    if (!spawn) return;

    this.deathScreen.hide();
    this.player.respawn(spawn);
    this.nearExitDoor = false;
    this.exitAdvanceConsumed = false;
    this.exitDoorArmed = false;
    this.doorCooldownUntil = performance.now() + 500;
    window.setTimeout(() => {
      this.exitDoorArmed = true;
    }, 400);

    this.events.emit('player:respawned', {
      playerId: this.profile.playerId,
      spawnLocation: spawn,
    });

    const pending = this.lootDrops.list().filter((d) => d.runId === this.world.getRunId()).length;
    this.events.emit('ui:toast', {
      message:
        pending > 0
          ? `Respawned at stage entrance — ${pending} loot bag${pending === 1 ? '' : 's'} still out there.`
          : 'Respawned at stage entrance — gear kept. Keep going.',
      kind: 'warn',
    });
    this.setState(GameState.Diving);
    this.syncCaptureBlocked();
    // Desktop only — on phones/tablets this triggers Chrome's pointer-lock
    // overlay and eats touch input until the page is reloaded.
    this.input.requestPointerLock();
  }

  /** Walk-over recovery for death loot bags in the current cave generation. */
  private tryRecoverLoot(): void {
    const recovered = this.lootDrops.tryPickup(
      this.player.state.position,
      this.world.getRunId(),
    );
    if (!recovered) return;

    this.inventory.addRunSlots(recovered.contents);
    this.syncRunCounters();

    const count = this.lootDrops.totalItems(recovered.contents);
    const summary = this.lootDrops.summarize(recovered.contents);

    for (const marker of this.deathMarkers) {
      if (marker.id === recovered.dropId) {
        marker.lootPending = false;
        break;
      }
    }

    this.events.emit('loot:recovered', { dropId: recovered.dropId, itemCount: count });
    this.events.emit('inventory:changed', { source: 'loot' });
    this.events.emit('ui:toast', {
      message: `Recovered loot: ${summary}`,
      kind: 'success',
    });
  }

  // ---------------------------------------------------------------------------
  // Inventory helpers
  // ---------------------------------------------------------------------------

  private trackDepth(): void {
    const depth = this.world.depthBelowSurface(this.player.state.position.y);
    if (depth > this.runInventory.depthReached) {
      this.runInventory.depthReached = depth;
    }
    if (depth > this.profile.highestDepthReached) {
      this.profile.highestDepthReached = depth;
    }
  }

  private collectBlock(type: BlockType): void {
    const itemId = blockTypeToItem(type);
    if (itemId) {
      this.inventory.addToRun(itemId, 1);
      this.events.emit('inventory:changed', { source: 'mine' });
    }
    this.syncRunCounters();
  }

  /** Keep legacy ActiveRunInventory counters mirrored for HUD / extract events. */
  private syncRunCounters(): void {
    this.runInventory.dirt = this.inventory.getRunAmount(ItemId.Dirt);
    this.runInventory.stone = this.inventory.getRunAmount(ItemId.Stone);
    this.runInventory.iron = this.inventory.getRunAmount(ItemId.IronOre);
    this.runInventory.gold = this.inventory.getRunAmount(ItemId.GoldOre);
  }

  private setState(next: GameState): void {
    if (this.state === next) return;
    const previous = this.state;
    this.state = next;
    this.events.emit('state:changed', { previous, next });
  }

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------

  private wireNetwork(): void {
    this.network.on('peer:block', (packet) => {
      if (packet.playerId === this.profile.playerId) return;
      this.applyingRemoteBlock = true;
      const previous = this.world.modifyBlock(packet.x, packet.y, packet.z, packet.blockType);
      this.applyingRemoteBlock = false;
      if (previous === null || previous === packet.blockType) return;
      this.events.emit('block:changed', {
        x: packet.x,
        y: packet.y,
        z: packet.z,
        previous,
        next: packet.blockType,
      });
      this.minimap.invalidate();
      this.events.emit('network:remote-block', {
        x: packet.x,
        y: packet.y,
        z: packet.z,
        blockType: packet.blockType,
      });
      if (this.multiplayer?.role === 'host') {
        this.persistSnapshot(false);
      }
    });

    this.network.on('peer:death', (packet) => {
      if (packet.playerId === this.profile.playerId) return;
      this.remotes?.setHealth(packet.playerId, 0);
      this.deathMarkers.push({
        id: `remote-death-${packet.sequence}`,
        kind: 'gravestone',
        position: { ...packet.deathLocation },
        ownerPlayerId: packet.playerId,
        createdAt: performance.now(),
        lootPending: false,
      });
      this.events.emit('network:remote-death', {
        playerId: packet.playerId,
        deathLocation: packet.deathLocation,
      });
      this.tryResetBossesOnPartyWipe();
    });

    this.network.on('peer:move', (packet) => {
      if (packet.playerId === this.profile.playerId) return;
      this.remotes?.applyMove(
        packet.playerId,
        packet.position,
        packet.yaw ?? 0,
        packet.health,
        packet.maxHealth,
      );
      this.events.emit('network:remote-move', {
        playerId: packet.playerId,
        position: packet.position,
        velocity: packet.velocity,
        yaw: packet.yaw ?? 0,
        health: packet.health ?? 100,
        maxHealth: packet.maxHealth ?? 100,
      });
    });
  }

  /** Remote player HP list for the HUD party strip. */
  getRemoteHealth(): Array<{
    playerId: string;
    displayName: string;
    health: number;
    maxHealth: number;
  }> {
    return this.remotes?.listHealth() ?? [];
  }

  private wireMultiplayerSession(session: MultiplayerSession): void {
    session.socket.on('room:player-joined', (player: { playerId: string; displayName: string }) => {
      this.remotes?.upsert(player.playerId, player.displayName);
      this.events.emit('ui:toast', {
        message: `${player.displayName} joined`,
        kind: 'info',
      });
      if (session.role === 'host') {
        this.broadcastStageEnter();
        this.broadcastEnemySnapshot();
      }
    });

    session.socket.on('room:player-left', (payload: { playerId: string }) => {
      this.remotes?.remove(payload.playerId);
      this.events.emit('ui:toast', {
        message: 'A miner left the dig',
        kind: 'warn',
      });
    });

    session.socket.on('peer:hello', (player: { playerId: string; displayName: string }) => {
      this.remotes?.upsert(player.playerId, player.displayName);
    });

    session.socket.on('stage:enter', (packet: StageEnterPacket) => {
      if (session.role === 'host') return;
      this.applyRemoteStage(packet);
    });

    session.socket.on('enemy:snapshot', (payload: { enemies: EnemySpawnPacket[] }) => {
      if (session.role === 'host') return;
      this.applyEnemySnapshot(payload.enemies ?? []);
    });

    session.socket.on('enemy:state', (packet: EnemyStatePacket) => {
      if (session.role === 'host') return;
      this.enemies.applyNetworkState(packet.enemies ?? []);
    });

    session.socket.on('enemy:hit', (packet: EnemyHitPacket) => {
      if (session.role !== 'host') return;
      if (packet.playerId === this.profile.playerId) return;
      const result = this.enemies.applyDamage(packet.enemyId, packet.damage, packet.knockDir);
      if (!result) return;
      if (result.killed) {
        this.events.emit('enemy:killed', { enemyId: packet.enemyId, name: result.name });
        this.onBossTargetMaybeCleared(result.name);
      }
      this.broadcastEnemyState();
    });

    session.socket.on('player:hurt', (packet: PlayerHurtPacket) => {
      if (packet.playerId !== this.profile.playerId) return;
      if (!this.player.state.alive) return;
      const killed = this.applyPlayerHurt(packet.amount, packet.sourceEnemyId);
      if (killed) {
        this.mining.setMining(false);
        this.handlePlayerDeath('combat', 0);
      }
    });

    session.socket.on('room:error', (payload: { message: string }) => {
      this.events.emit('ui:toast', { message: payload.message, kind: 'warn' });
    });
  }

  private applyRemoteStage(packet: StageEnterPacket): void {
    const needsRegen =
      packet.seed !== this.world.getSeed() || packet.runId !== this.world.getRunId();
    if (needsRegen) {
      this.lootDrops.clearAll();
      this.mining.clearAll();
      this.enemies.clearAll();
      const stage = Math.max(1, packet.stage);
      const n = this.config.levelCount;
      const columnStart = stage - (((stage - 1) % n) + 1) + 1;
      this.world.regenerate(packet.seed, columnStart);
      if (packet.runId > 0) {
        this.world.setRunId(packet.runId);
      }
      this.world.applyDigs(packet.digs ?? []);
      this.events.emit('world:ready', {
        width: this.config.worldWidth,
        height: this.config.worldHeight,
        depth: this.config.worldDepth,
        levelCount: this.config.levelCount,
        seed: packet.seed,
      });
    } else {
      // Same cave column — still apply any digs the host sent.
      this.world.applyDigs(packet.digs ?? []);
    }
    this.enterStage(Math.max(1, packet.stage), true);
    if (packet.enemies) {
      this.applyEnemySnapshot(packet.enemies);
    }
  }

  private applyEnemySnapshot(enemies: EnemySpawnPacket[]): void {
    this.enemies.applySnapshot(
      enemies.map((e) => ({
        id: e.id,
        kind: (Object.values(EnemyKind) as string[]).includes(e.kind)
          ? (e.kind as EnemyKind)
          : EnemyKind.Crawler,
        name: e.name,
        health: e.health,
        maxHealth: e.maxHealth,
        damage: e.damage,
        speed: e.speed,
        aggroRange: e.aggroRange,
        attackCooldown: e.attackCooldown,
        flying: e.flying,
        position: e.position,
        phase: e.phase,
        invulnerable: e.invulnerable,
        burrowed: e.burrowed,
        armorCracked: e.armorCracked,
        linkedBossId: e.linkedBossId ?? null,
      })),
    );
    this.syncBossExitLock(true);
    this.events.emit('enemy:spawned', {
      stage: this.runInventory.currentLevel,
      count: this.enemies.aliveCount(),
    });
    if (isBossStage(this.runInventory.currentLevel) && !this.enemies.bossCleared()) {
      const kind = bossKindForStage(this.runInventory.currentLevel);
      this.events.emit('ui:toast', {
        message: `${ENEMY_CATALOG[kind].name} blocks the way — defeat it to unlock the exit.`,
        kind: 'warn',
      });
    }
  }

  private broadcastEnemySnapshot(): void {
    if (!this.multiplayer || this.multiplayer.role !== 'host') return;
    const enemies = this.enemies.exportSnapshot().map((e) => ({
      ...e,
      kind: e.kind as string,
    }));
    this.multiplayer.socket.emit('enemy:snapshot', { enemies });
  }

  private broadcastEnemyState(): void {
    if (!this.multiplayer || this.multiplayer.role !== 'host') return;
    const packet: EnemyStatePacket = {
      enemies: this.enemies.exportState(),
      sequence: ++this.enemyStateSequence,
    };
    this.multiplayer.socket.emit('enemy:state', packet);
  }

  private sendEnemyHitIntent(enemyId: string, damage: number, knockDir: Vector3D): void {
    if (!this.multiplayer || this.multiplayer.role !== 'client') return;
    const packet: EnemyHitPacket = {
      playerId: this.profile.playerId,
      enemyId,
      damage,
      knockDir,
    };
    this.multiplayer.socket.emit('enemy:hit', packet);
  }

  private lookKnockDir(): Vector3D {
    this.fillAimDirection(this.aimDir);
    return { x: this.aimDir.x, y: this.aimDir.y, z: this.aimDir.z };
  }

  private broadcastStageEnter(): void {
    if (!this.multiplayer || this.multiplayer.role !== 'host') return;
    const packet: StageEnterPacket = {
      stage: Math.max(1, this.runInventory.currentLevel),
      seed: this.world.getSeed(),
      runId: this.world.getRunId(),
      digs: this.world.exportDigs() as DigPatch[],
      enemies: this.enemies.exportSnapshot().map((e) => ({
        ...e,
        kind: e.kind as string,
      })),
    };
    emitStageEnter(this.multiplayer, packet);
  }

  private persistSnapshot(force: boolean): void {
    if (!this.multiplayer || this.multiplayer.role !== 'host') return;
    const now = performance.now();
    if (!force && now - this.lastSnapshotSaveAt < this.snapshotSaveIntervalMs) return;
    this.lastSnapshotSaveAt = now;
    saveWorldSnapshot(this.multiplayer, {
      name: this.multiplayer.snapshot.name,
      seed: this.world.getSeed(),
      runId: this.world.getRunId(),
      stage: Math.max(1, this.runInventory.currentLevel),
      digs: this.world.exportDigs() as DigPatch[],
    });
  }

  /** Persist meta-progression + run bag (+ singleplayer world) to localStorage. */
  private persistPlayerSave(force: boolean): void {
    if (!this.running && !force) return;
    const now = performance.now();
    if (!force && now - this.lastPlayerSaveAt < this.playerSaveIntervalMs) return;
    this.lastPlayerSaveAt = now;

    this.inventory.syncProfile(this.profile);
    this.syncRunCounters();

    const hotbar = this.inventory.getHotbar();
    const runBag = this.inventory.listRunSlots().map((s) => ({
      itemId: s.itemId,
      amount: s.amount,
    }));
    const run = { ...this.runInventory };
    const prior = loadPlayerSave(this.profile.playerId, this.profile.displayName);

    const data: PlayerSaveData = {
      version: 1,
      profile: {
        playerId: this.profile.playerId,
        displayName: this.profile.displayName,
        walletCredits: this.profile.walletCredits,
        unlockedPickaxeTiers: [...this.profile.unlockedPickaxeTiers],
        equippedPickaxeTier: this.profile.equippedPickaxeTier,
        totalDeaths: this.profile.totalDeaths,
        totalExtractions: this.profile.totalExtractions,
        highestDepthReached: this.profile.highestDepthReached,
        highestLevelReached: this.profile.highestLevelReached,
      },
      bank: this.inventory.listBankSlots().map((s) => ({
        itemId: s.itemId,
        amount: s.amount,
      })),
      ownedGear: this.inventory.listOwnedGear(),
      hotbar: {
        slots: [...hotbar.slots],
        activeIndex: hotbar.activeIndex,
      },
      runBag,
      run,
      singleplayer: this.multiplayer
        ? (prior?.singleplayer ?? null)
        : {
            seed: this.world.getSeed(),
            runId: this.world.getRunId(),
            stage: Math.max(1, this.runInventory.currentLevel || 1),
            digs: this.world.exportDigs() as DigPatch[],
            position: {
              x: this.player.state.position.x,
              y: this.player.state.position.y,
              z: this.player.state.position.z,
            },
            runBag,
            run,
          },
      updatedAt: Date.now(),
    };

    savePlayerSave(data);
  }

  getGraphicsQuality(): GraphicsQuality {
    return this.graphicsSettings.quality;
  }

  setGraphicsQuality(quality: GraphicsQuality): void {
    this.graphicsSettings = { quality };
    saveGraphicsSettings(this.graphicsSettings);
    this.applyGraphics(resolveGraphics(this.graphicsSettings), true);
  }

  private applyGraphics(resolved: ResolvedGraphics, resize: boolean): void {
    this.graphics = resolved;
    this.applyPixelRatio();
    if (resize) {
      this.renderer.setSize(window.innerWidth, Math.max(1, window.innerHeight));
    }

    const fog = this.scene.fog;
    if (fog instanceof Fog) {
      fog.near = resolved.fogNear;
      fog.far = resolved.fogFar;
    }

    this.lampLight.intensity = resolved.lampIntensity;
    this.lampLight.distance = resolved.lampDistance;
    this.rimLight.visible = resolved.rimLight;
    this.world.setReceiveShadow(resolved.shadowReceive);
    this.world.setPortalLightsEnabled(resolved.portalLights);

    // Slightly cheaper directional fill on low tier.
    this.fillLight.intensity = resolved.tier === 'low' ? 0.35 : 0.55;
    this.sunLight.intensity = resolved.tier === 'low' ? 0.95 : 1.15;

    this.camera.far = Math.max(48, resolved.fogFar + 12);
    this.camera.updateProjectionMatrix();
  }

  private applyPixelRatio(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, this.graphics.pixelRatioCap);
    this.renderer.setPixelRatio(dpr * this.graphics.renderScale);
  }

  private installLights(): {
    sun: DirectionalLight;
    fill: DirectionalLight;
    lamp: PointLight;
    rim: PointLight;
  } {
    const ambient = new AmbientLight(0x9aabbc, 0.85);
    this.scene.add(ambient);

    const sun = new DirectionalLight(0xfff2d6, 1.15);
    sun.position.set(40, 80, 20);
    this.scene.add(sun);

    const fill = new DirectionalLight(0x6a8aac, 0.55);
    fill.position.set(-30, 20, -40);
    this.scene.add(fill);

    // Player lantern — brighter and longer reach so caves read clearly.
    const lamp = new PointLight(0xffe6c8, 2.4, 28, 1.35);
    lamp.position.set(0, 0.15, 0.2);
    this.camera.add(lamp);

    const rim = new PointLight(0xa8c4e8, 0.7, 14, 2);
    rim.position.set(0, 0.4, -0.6);
    this.camera.add(rim);

    return { sun, fill, lamp, rim };
  }

  private readonly onResize = (): void => {
    const w = window.innerWidth;
    const h = Math.max(1, window.innerHeight);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.applyPixelRatio();
    this.renderer.setSize(w, h);
  };

  private syncBossExitLock(force = false): void {
    const locked = this.isBossExitLocked();
    const wasLocked = this.bossExitLocked;
    if (!force && locked === wasLocked) return;
    this.bossExitLocked = locked;
    this.world.setExitLocked(locked);
    // Clients learn of a clear via enemy:state — emit defeat when the lock drops.
    if (
      wasLocked &&
      !locked &&
      isBossStage(this.runInventory.currentLevel) &&
      this.multiplayer?.role === 'client'
    ) {
      this.events.emit('boss:defeated', {
        stage: this.runInventory.currentLevel,
        name: ENEMY_CATALOG[bossKindForStage(this.runInventory.currentLevel)].name,
      });
    }
  }

  private onBossTargetMaybeCleared(name: string): void {
    if (!isBossStage(this.runInventory.currentLevel)) return;
    if (!this.enemies.bossCleared()) return;
    const wasLocked = this.bossExitLocked;
    this.syncBossExitLock(true);
    if (wasLocked) {
      this.events.emit('boss:defeated', {
        stage: this.runInventory.currentLevel,
        name,
      });
    }
  }
}

function cryptoRandomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}
